import { Attachment } from "@/api/attachment";
import { useGetSelectionFormData } from "@/api/cms";
import { uploadImage } from "@/api/image";
import {
  useCreateSection,
  useDeleteSection,
  useGetSectionById,
  useUpdateSection,
} from "@/api/section";
import { SectionForm } from "@/components/cms/selection/SectionForm";
import DragAndDropLoadImage, {
  ACCEPTED_IMAGE_TYPES,
} from "@/components/form-fields/DragAndDropLoadImage";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import { Button } from "@/components/ui/button";
import { CustomAlertDialog } from "@/components/ui/custom-alert-dialog";
import { Form } from "@/components/ui/form";
import { useAuth } from "@/context/auth/AuthContext";
import { error, success, useToast } from "@/hooks/use-toast";
import {
  constructLocaleDict,
  getMainLanguage,
  multilingualFieldSchema,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout";
import { sectionContentSchema, sectionSchema } from "@/lib/cms/section-types";
import { generateImageName } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";

const formSchema = sectionSchema.omit({ id: true }).extend({
  content: multilingualFieldSchema(
    sectionContentSchema.omit({ id: true, sectionId: true }),
  ),
  thumbnail: z
    .instanceof(File, { message: "form.error.required" })
    .refine(
      (file) => {
        return ACCEPTED_IMAGE_TYPES.includes(file.type);
      },
      {
        message: "form.error.fileNotSupported",
      },
    )
    .optional(),
});
type FormData = z.infer<typeof formSchema>;

const EditSection = () => {
  const { t } = useTranslation();
  const { instanceData } = useAuth();
  const { toast } = useToast();
  const { mutateAsync: createSection } = useCreateSection();
  const { mutateAsync: updateSection } = useUpdateSection();
  const { mutateAsync: deleteSection } = useDeleteSection();

  const { data: selectionPageData } = useGetSelectionFormData();

  const navigate = useNavigate();
  const { id } = useParams();
  const { data: sectionData } = useGetSectionById(id);
  const [needToUploadThumbnail, setNeedToUploadThumbnail] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      thumbnail: undefined,
      content: constructLocaleDict(() => {
        return {
          title: "",
          content: "",
          published: false,
        };
      }),
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (sectionData) {
      setNeedToUploadThumbnail(!id);
      form.reset({
        thumbnail: undefined,
        content: constructLocaleDict((lang) => {
          const section = sectionData?.content?.[lang];
          return {
            title: section?.title ?? "",
            content: section?.content ?? "",
            published: section?.published ?? false,
          };
        }),
      });
    }
  }, [form, id, sectionData]);

  const onSubmit = async (submittedData: FormData) => {
    if (instanceData?.CMS?.id == null) {
      console.error("Tried to submit but there is no CMS");
      return;
    }

    if (!submittedData.thumbnail && needToUploadThumbnail) {
      toast({
        title: t("error"),
        description: t("cms.selection.editSection.error.thumbnailRequired"),
        variant: "error",
        duration: 1000,
      });
      return;
    }

    const response = submittedData.thumbnail
      ? await uploadImage(
          submittedData.thumbnail,
          {
            name: generateImageName({
              primaryType: "thumbnail",
              instanceName: instanceData.name,
              pageNameInDefaultLanguage: "section",
              title: submittedData.content[getMainLanguage()]?.title,
            }),
            position: "sectionThumbnail",
            docId: "",
            published: false,
          } as Attachment,
          instanceData.s3Bucket,
          instanceData.id,
        )
      : null;

    if (!id) {
      await createSection(
        {
          content: submittedData.content,
          // response can't be null here because of previous validation
          thumbnailId: response!.id,
        },
        {
          onError: (e) => {
            console.error("Error", e);
            toast(
              error({
                description: t(
                  "cms.selection.editSection.error.createSectionFailed",
                ),
              }),
            );
          },
          onSuccess: () => {
            toast(
              success({
                description: t(
                  "cms.selection.editSection.success.createSection",
                ),
              }),
            );
            navigate("/cms/selection/");
          },
        },
      );
    } else {
      await updateSection(
        {
          id: id,
          section: {
            content: submittedData.content,
            // sectionData.thumbnail shouldn't be undefined here because we're editing an already existing section
            // The only way it is undefined is if the image was deleted on the db side or the data failed to load
            // In these cases, response is not null because of previous validation
            thumbnailId: response ? response.id : sectionData!.thumbnail!.id,
          },
        },
        {
          onError: (e) => {
            console.error("Error", e);
            toast(
              error({
                description: t(
                  "cms.selection.editSection.error.updateSectionFailed",
                ),
              }),
            );
          },
          onSuccess: () => {
            toast(
              success({
                description: t(
                  "cms.selection.editSection.success.updateSection",
                ),
              }),
            );
            navigate("/cms/selection/section/" + id);
          },
        },
      );
    }
  };

  const handleDelete = () => {
    if (!id) {
      throw new Error("Tried to delete section but id is undefined");
    }
    deleteSection(id, {
      onError: (e) => {
        console.error("Error", e);
        toast(
          error({
            description: t(
              "cms.selection.editSection.error.deleteSectionFailed",
            ),
          }),
        );
      },
      onSuccess: () => {
        toast(
          success({
            description: t("cms.selection.editSection.success.deleteSection"),
          }),
        );
      },
    });
    navigate("/cms/selection/");
  };

  const handleDeleteThumbnail = () => {
    setNeedToUploadThumbnail(true);
  };

  return (
    <CMSLayout
      formIds={["editSection"]}
      pageName={
        id
          ? t("cms.selection.editSection.title")
          : t("cms.selection.editSection.title.addSection")
      }
      breadcrumbs={[
        {
          label:
            selectionPageData?.[getMainLanguage()]?.pageName ??
            t("cmsTabSelection"),
          to: "/cms/selection",
        },

        ...(id
          ? [
              {
                label: sectionData?.content[getMainLanguage()]?.title ?? "",
                to: "/cms/selection/section/" + id,
              },
              {
                label: t("cms.selection.editSection.title"),
              },
            ]
          : [
              {
                label: t("cms.selection.editSection.title.addSection"),
              },
            ]),
      ]}
    >
      <Form {...form}>
        <form
          id="editSection"
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-8 w-full"
        >
          <div className="flex flex-row w-full justify-between items-center">
            <p className="text-gray-950 font-semibold text-lg">
              {t("cms.selection.editSection.thumbnail.title")}
            </p>
            {id && (
              <CustomAlertDialog
                title={t("cms.selection.editSection.deleteAlert.title")}
                description={t(
                  "cms.selection.editSection.section.deleteAlert.description",
                )}
                confirmButton={
                  <Button
                    type="button"
                    onClick={handleDelete}
                    variant={"destructive"}
                  >
                    {t("cms.selection.editSection.section.delete")}
                  </Button>
                }
              >
                <Button type="button" variant={"destructive"}>
                  {t("cms.selection.editSection.section.delete")}
                </Button>
              </CustomAlertDialog>
            )}
          </div>
          {sectionData?.thumbnail?.mediumPublicUrl && !needToUploadThumbnail ? (
            <div className="max-h-[600px] relative w-fit min-w-[300px] hover:cursor-pointer">
              <img
                src={sectionData.thumbnail.mediumPublicUrl}
                alt="image not found"
              />
              <div
                className="absolute inset-0 bottom-0 left-0 gap-1.5 bg-black bg-opacity-70 opacity-0 hover:opacity-100 duration-300"
                onClick={handleDeleteThumbnail}
              >
                <div className="flex flex-row justify-center items-center w-full h-full">
                  <Trash2 color="#dc2626" size={20} />
                  <span className="font-semibold text-base text-red-600">
                    {t("cms.editSection.thumbnail.delete")}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <DragAndDropLoadImage
              control={form.control}
              config={{ name: "thumbnail", type: "inputFile" }}
              getValues={form.getValues}
              className="bg-white border border-solid"
            />
          )}
          <div className="flex gap-4">
            <DoubleMultilingualField
              corner={(lang) => (
                <Switch dispLabel={true} name={`content.${lang}.published`} />
              )}
              render={(lang) => <SectionForm lang={lang} />}
            />
          </div>
        </form>
      </Form>
    </CMSLayout>
  );
};

export default EditSection;
